import { SearchX, ShieldX, WifiOff } from 'lucide-react';
import { useEffect, useEffectEvent, type ReactNode } from 'react';
import { Link } from 'react-router';
import { t } from '@ticarishift/i18n';
import { Button, EmptyState, Spinner, Wordmark } from '@ticarishift/ui';
import { permissionLabel, type PlatformPermission } from '../../shared/auth/permissions.ts';
import { PlatformBadge } from '../../shared/ui/badges.tsx';

/** 403 (08 §4.4): gereken yetkinin adı; rol platform sahibinden istenir. */
export function ForbiddenPage({ permission, home }: { permission: PlatformPermission; home: string }) {
  return (
    <EmptyState
      variant="forbidden"
      icon={ShieldX}
      headingLevel={2}
      title={t('backoffice.system.forbiddenTitle')}
      body={t('backoffice.system.forbiddenBody', { permission: permissionLabel(permission) })}
      action={
        <Button asChild variant="secondary">
          <Link to={home}>{t('backoffice.system.goHome')}</Link>
        </Button>
      }
    />
  );
}

/** 404: rolün ana sayfasına dönüş. */
export function NotFoundPage({ home = '/' }: { home?: string }) {
  return (
    <EmptyState
      variant="no-results"
      icon={SearchX}
      headingLevel={2}
      title={t('backoffice.system.notFoundTitle')}
      body={t('backoffice.system.notFoundBody')}
      action={
        <Button asChild variant="secondary">
          <Link to={home}>{t('backoffice.system.goHome')}</Link>
        </Button>
      }
    />
  );
}

function FullPage({ children }: { children: ReactNode }) {
  return (
    <main id="main" className="mx-auto flex min-h-dvh max-w-lg flex-col items-center justify-center gap-6 px-4 py-12 text-center">
      <div className="flex items-center gap-3">
        <Wordmark height={28} />
        <PlatformBadge />
      </div>
      {children}
    </main>
  );
}

/** Oturum ve rol yüklenirken: boş ekran yerine marka ve gösterge. */
export function LoadingScreen({ label = t('backoffice.system.loading') }: { label?: string }) {
  return (
    <FullPage>
      <Spinner size={24} label={label} />
    </FullPage>
  );
}

/**
 * "Bağlantı kurulamadı" (S09-K1 deseni): açılışta sunucuya ulaşılamadı; oturum korunur. 2,5 sn sonra bir kez
 * kendiliğinden, bağlantı gelince (`online`) yeniden denenir.
 */
export function ConnectionErrorScreen({ onRetry, retrying }: { onRetry: () => void; retrying?: boolean }) {
  const retry = useEffectEvent(onRetry);

  useEffect(() => {
    const timer = window.setTimeout(() => retry(), 2500);
    const onOnline = () => retry();
    window.addEventListener('online', onOnline);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('online', onOnline);
    };
  }, []);

  return (
    <FullPage>
      <div role="alert" className="grid justify-items-center gap-3">
        <span className="inline-flex size-12 items-center justify-center rounded-full bg-status-attention-bg text-status-attention">
          <WifiOff aria-hidden size={24} strokeWidth={1.75} />
        </span>
        <h1 className="text-h1 text-fg">{t('backoffice.system.connectionTitle')}</h1>
        <p className="text-body text-fg-muted">{t('backoffice.system.connectionBody')}</p>
      </div>
      <div className="flex flex-wrap justify-center gap-3">
        <Button onClick={onRetry} loading={retrying}>
          {t('common.actions.retry')}
        </Button>
        <Button asChild variant="secondary">
          <Link to="/login">{t('backoffice.system.otherAccount')}</Link>
        </Button>
      </div>
    </FullPage>
  );
}
